import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarRange, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { inr, num } from "@/lib/format";

const RUNNING = ["queued", "reading", "loading"];

const Summary = ({ job, onOpen }) => {
  const s = job.summary || {};
  return (
    <div className="mt-3 text-xs text-slate-600 space-y-1" data-testid="register-summary">
      <div className="flex items-center gap-1.5 text-emerald-700 font-semibold"><CheckCircle2 className="h-4 w-4" />Loaded {num(s.sale_lines)} sale lines · NSV {inr(s.nsv, { compact: true })}</div>
      <div>Months replaced: <b data-testid="register-summary-months">{(s.months || []).join(", ") || "—"}</b></div>
      <div>Zones updated: {(s.zones || []).join(", ") || "—"}</div>
      <div>Target days: {num(s.target_days)} {s.target_months?.length ? `(${s.target_months.join(", ")})` : ""} · Stock rows: {num(s.stock_rows)}</div>
      {s.new_stores?.length > 0 && <div>New stores added: {s.new_stores.join(", ")}</div>}
      <div className="text-slate-400">Sheets: {(job.sheets || []).map((x) => `${x.name} (${x.kind})`).join(" · ")}</div>
      {job.batch_id && <button data-testid="register-open-batch-btn" onClick={onOpen} className="text-black font-semibold underline">Open batch</button>}
    </div>
  );
};

export const RegisterUpload = () => {
  const [job, setJob] = useState(null);
  const [busy, setBusy] = useState(false);
  const ref = useRef();
  const nav = useNavigate();
  useEffect(() => { api.get("/register/jobs").then((r) => r.data[0] && setJob(r.data[0])); }, []);
  useEffect(() => {
    if (!job || !RUNNING.includes(job.status)) return undefined;
    const t = setInterval(() => api.get(`/register/jobs/${job.id}`).then((r) => {
      setJob(r.data);
      if (r.data.status === "done") toast.success("Sale register loaded — dashboards updated");
      if (r.data.status === "failed") toast.error(r.data.error || "Register upload failed");
    }), 2500);
    return () => clearInterval(t);
  }, [job?.id, job?.status]); // eslint-disable-line
  const pick = async (f) => {
    if (!f) return;
    if (!/\.(xlsx|xlsm)$/i.test(f.name)) return toast.error("Upload the L2L sale register as an .xlsx workbook");
    setBusy(true);
    const fd = new FormData(); fd.append("file", f);
    try { const { data } = await api.post("/register/upload", fd, { timeout: 600000 }); setJob(data); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); ref.current.value = ""; }
  };
  const running = busy || (job && RUNNING.includes(job.status));
  return (
    <div className="card p-5 mb-6" data-testid="register-upload-card">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="max-w-2xl">
          <div className="font-display font-semibold text-slate-900 flex items-center gap-2"><CalendarRange className="h-4 w-4" />Monthly L2L sale register</div>
          <div className="text-xs text-slate-500 mt-1">Drop the month's L2L workbook (e.g. <i>L2L Sale Data Nov'26.xlsx</i>). Sale sheets for this year and last year, Day Wise Target, Closing Stock and Store Master are detected automatically; each store goes to its own zone and that month's data is replaced.</div>
        </div>
        <input ref={ref} type="file" accept=".xlsx,.xlsm" className="hidden" onChange={(e) => pick(e.target.files[0])} data-testid="register-file-input" />
        <button data-testid="register-upload-btn" disabled={running} onClick={() => ref.current.click()} className="h-10 px-4 rounded-md btn-red font-semibold text-sm disabled:opacity-50 flex items-center gap-2">
          {running && <Loader2 className="h-4 w-4 animate-spin" />}{running ? "Processing…" : "Upload sale register"}
        </button>
      </div>
      {job && (
        <div className="mt-4 border-t pt-3" data-testid="register-job-status">
          <div className="text-xs text-slate-500"><b className="text-slate-800">{job.file_name}</b> · {job.status === "done" ? "completed" : job.status} {RUNNING.includes(job.status) && `· ${job.step || ""}`}</div>
          {job.status === "failed" && <div className="mt-2 text-xs text-red-700 flex items-center gap-1.5" data-testid="register-job-error"><AlertTriangle className="h-4 w-4" />{job.error}</div>}
          {job.status === "done" && <Summary job={job} onOpen={() => nav(`/batches/${job.batch_id}`)} />}
        </div>
      )}
    </div>
  );
};
